import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import AddIcon from '@material-ui/icons/Add';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import SortIcon from '@material-ui/icons/Sort';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import IconButton from '@material-ui/core/IconButton';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import GridOnIcon from '@material-ui/icons/GridOn';
import RefreshIcon from '@material-ui/icons/Refresh';
import Tooltip from 'components/Tooltip/Tooltip';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import useStyles, { useExtProdStyles } from '../styles/moduleStyles';

import { ViewMode } from '../viewModes';
import { Currency } from 'enums';
import { AmountValue } from 'common/interfaces';
import { RootStore } from 'stores/rootStore';
import ExternalProductModal from './ExternalProductModal';
import ExternalProduct from './ExternalProduct';
import { ExternalProductsStore } from 'stores/workflowStores/finances/externalProducts/externalProducts';
import {
  ExternalProductData,
  ExternalProductsData,
  ExtProdSortBy,
  ExtProductCategory,
  ExtProductType,
  SimpleDeposit,
} from './model';
import { WorkflowPlanStore } from 'stores/workflowStores/newWorkflowPlanStore/workflowPlanStore';
import ListItemText from '@material-ui/core/ListItemText/ListItemText';
import { ViewKeys } from 'stores/userStoreKC';
import TextSwitch from 'components/FormFields/TextSwitch';
import ExternalProductsTable from './ExternalProductsTable';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import Message, { MessageVariant } from '../../../../../components/Message/Message';

interface Props {
  accountId: string;
  rootStore: RootStore;
  readOnly?: boolean;
  userMode?: boolean;
  viewMode?: ViewMode;
  currency: Currency;
  refresh?: () => void;
  productsData?: ExternalProductsData;
  plan?: boolean;
  overview?: boolean;
  original?: boolean;
  defaultInflation: number;
  defaultInflationType: string;
  collapsed?: boolean;
  planStore?: WorkflowPlanStore;
  currentAccount: { title: string; balance: AmountValue; profit: number };
}

enum AggCategories {
  FINANCIAL = 'financial',
  PROPERTY = 'property',
  LOAN = 'loan',
  EDWARD = 'edward',
  FINANCIAL_PRODUCT = 'financialProduct',
}

export function sortExtProducts(
  a: ExternalProductData,
  b: ExternalProductData,
  sortBy: ExtProdSortBy,
  orderDir: boolean,
) {
  const preSort = {
    [ExtProductCategory.EDWARD]: 0,
    [ExtProductCategory.FINANCIAL_PRODUCT]: 1,
    [ExtProductCategory.PROPERTY]: 2,
    [ExtProductCategory.LOAN]: 3,
    [ExtProductCategory.NONE]: 4,
  };

  switch (sortBy) {
    case ExtProdSortBy.TITLE:
      return a.title.localeCompare(b.title) * (orderDir ? 1 : -1);
    case ExtProdSortBy.INST:
      const categorySort = preSort[a.category] - preSort[b.category];
      if (categorySort === 0) {
        if (a.financialInstitution?.title === b.financialInstitution?.title) {
          return a.title.localeCompare(b.title) * (orderDir ? 1 : -1);
        } else {
          return (
            (a.financialInstitution?.title || '').localeCompare(b.financialInstitution?.title || '') *
            (orderDir ? 1 : -1)
          );
        }
      } else {
        return categorySort;
      }
    case ExtProdSortBy.BALANCE:
      return (a.balance.value - b.balance.value) * (orderDir ? 1 : -1);
    default:
      return 0;
  }
}

const ExternalProducts: React.FC<Props> = ({
  accountId,
  rootStore,
  viewMode,
  currency,
  refresh,
  productsData,
  defaultInflation,
  defaultInflationType,
  readOnly,
  userMode,
  plan,
  overview,
  original,
  collapsed,
  planStore,
  currentAccount,
}) => {
  const { userStore } = rootStore.stores;
  const { sortBy, orderDir } = userStore.view[ViewKeys.EXTERNAL_PRODUCTS];
  const { t } = useTranslation();
  const classes = useStyles({ plan });
  const extProdClasses = useExtProdStyles({ detail: viewMode !== ViewMode.WF, plan: plan || overview });

  const [extProductsStore] = useState(new ExternalProductsStore(rootStore, accountId, viewMode));
  const [category, setCategory] = useState<ExtProductCategory | undefined>(undefined);
  const [productId, setProductId] = useState<string | undefined>(undefined);
  const [isExtProductOpen, setIsExtProductOpen] = useState(false);
  const [showDetail, setShowDetail] = useState<ExtProductCategory[]>(
    collapsed ? [] : [ExtProductCategory.FINANCIAL_PRODUCT, ExtProductCategory.PROPERTY, ExtProductCategory.LOAN],
  );
  const [mainAnchorEl, setMainAnchorEl] = useState<null | HTMLElement>(null);
  const [newProdMenuOpen, setNewProdMenuOpen] = useState(false);
  const [orderingOpen, setOrderingOpen] = useState(false);
  const [tableView, setTableView] = useState(plan || overview ? productsData?.tableLayout : false);
  const [confirmDialog, setConfirmDialog] = useState<string | undefined>(undefined);
  const [filtersVisible, setFilterVisible] = useState(false);

  const toggleFilterVisibility = () => {
    setFilterVisible(!filtersVisible);
  };

  const data = productsData ? productsData : extProductsStore.allProducts || undefined;

  const tablet = useMediaQuery('@media (max-width: 1200px)', { noSsr: true });

  const counts = {
    [AggCategories.FINANCIAL]:
      data?.externalProducts.filter((p) =>
        [ExtProductCategory.FINANCIAL_PRODUCT, ExtProductCategory.EDWARD].includes(p.category),
      ).length || 0,
    [AggCategories.PROPERTY]:
      data?.externalProducts.filter((p) => p.category === ExtProductCategory.PROPERTY).length || 0,
    [AggCategories.LOAN]: data?.externalProducts.filter((p) => p.category === ExtProductCategory.LOAN).length || 0,
    [AggCategories.EDWARD]: data?.externalProducts.filter((p) => p.category === ExtProductCategory.EDWARD).length || 0,
    [AggCategories.FINANCIAL_PRODUCT]:
      data?.externalProducts.filter((p) => p.category === ExtProductCategory.FINANCIAL_PRODUCT).length || 0,
  };

  const linkedAccounts = data?.externalProducts.some((p) => !!p.accountId);

  useEffect(() => {
    if (!productsData) {
      extProductsStore.fetchAllProducts();
    }
    setShowDetail(
      collapsed ? [] : [ExtProductCategory.FINANCIAL_PRODUCT, ExtProductCategory.PROPERTY, ExtProductCategory.LOAN],
    );
  }, [accountId, productsData, extProductsStore, collapsed]); //collapsed

  const handleProductModalClose = () => {
    setIsExtProductOpen(false);
    extProductsStore.clearData();
    planStore && planStore.setEditExtProduct(null);
  };

  const editProduct = (productId: string) => {
    setProductId(productId);
    setIsExtProductOpen(true);
  };

  const deleteProduct = (productId: string) => {
    setConfirmDialog(productId);
  };

  const editExtProductId = planStore?.editExtProduct;

  useEffect(() => {
    editExtProductId && editProduct(editExtProductId);
  }, [editExtProductId]);

  const handleRefresh = () => {
    if (refresh) {
      refresh();
    } else {
      extProductsStore.fetchAllProducts();
    }
  };

  const toggleCategoryView = (category: ExtProductCategory) => {
    const current = new Array(...showDetail);
    if (current.includes(category)) {
      current.splice(current.findIndex((c) => c === category));
    } else {
      current.push(category);
    }
    setShowDetail([...current]);
  };

  const addProduct = (addCategory?: ExtProductCategory) => {
    setCategory(addCategory);
    setProductId(undefined);
    setIsExtProductOpen(true);
    setNewProdMenuOpen(false);
  };

  const setSorting = (sort: ExtProdSortBy) => {
    userStore.setViewSetting(ViewKeys.EXTERNAL_PRODUCTS, {
      ...userStore.view[ViewKeys.EXTERNAL_PRODUCTS],
      sortBy: sort,
    });
    setOrderingOpen(false);
  };

  const setOrder = (dir: boolean) => {
    //setOrderDir(dir);
    userStore.setViewSetting(ViewKeys.EXTERNAL_PRODUCTS, {
      ...userStore.view[ViewKeys.EXTERNAL_PRODUCTS],
      orderDir: dir,
    });
  };

  const toggleTableView = async () => {
    await extProductsStore.setTableView(!tableView);
    setTableView(!tableView);
  };

  const selectedLabel = (sortByLabel: ExtProdSortBy) => {
    return sortByLabel === sortBy ? (
      <strong>{t(`externalProduct_sortBy_${sortByLabel}`)}</strong>
    ) : (
      t(`externalProduct_sortBy_${sortByLabel}`)
    );
  };

  const mainHeader = () => (
    <>
      {data && !data.addingEnabled && (
        <Grid container>
          <Grid item xs={12}>
            <Message
              id={`plan_max_external_products`}
              message={t('externalProducts_maxProducts')}
              variant={MessageVariant.INFO}
              permanent
            />
          </Grid>
        </Grid>
      )}

      <Grid
        container
        item
        spacing={0}
        alignItems="center"
        style={{
          marginBottom: '24px',
          position: 'relative',
          zIndex: 1,
          maxWidth: tablet ? '100%' : `calc(100% - ${tableView && !tablet ? 140 : 0}px)`,
          width: tablet ? 'auto' : undefined,
        }}
        xs={10}
      >
        <Grid item>
          <Typography variant="h2" className={classes.moduleTitle} style={tablet ? { minWidth: 0 } : undefined}>
            {t('externalProducts_title')}
          </Typography>
        </Grid>

        {plan && (
          <>
            <Grid
              item
              xs={2}
              style={{ marginLeft: plan || overview ? undefined : '16px', maxWidth: tablet ? 0 : '140px' }}
            >
              {tableView && !tablet && data?.addingEnabled && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={(e) => {
                    setMainAnchorEl(e.currentTarget);
                    setNewProdMenuOpen(true);
                  }}
                  startIcon={<AddIcon fontSize="medium" />}
                >
                  {t('general_add')}
                </Button>
              )}
            </Grid>
            {!tablet && (
              <>
                <div style={{ display: 'flex', flexGrow: 1 }} />
                <Grid item>{t('externalProducts_view')}</Grid>
                <Grid item xs={2} style={{ maxWidth: '100px', marginLeft: '24px' }}>
                  <TextSwitch
                    id="view"
                    checked={tableView}
                    afterChange={toggleTableView}
                    options={[{ icon: <ViewModuleIcon /> }, { icon: <GridOnIcon /> }]}
                  />
                </Grid>
              </>
            )}
          </>
        )}

        {(!tableView || tablet) && plan && (
          <div style={{ display: 'flex', minWidth: '140px', width: '140px', justifyContent: 'space-evenly' }}>
            <IconButton
              size="medium"
              onClick={(e) => {
                setMainAnchorEl(e.currentTarget);
                setOrderingOpen(true);
              }}
              className={classes.sortIcon}
            >
              <SortIcon fontSize="inherit" />
            </IconButton>
            <IconButton
              size="medium"
              onClick={() => setOrder(!orderDir)}
              className={`${classes.sortIcon} ${classes.orderIcon} ${orderDir}`}
            >
              <ArrowRightAltIcon fontSize="inherit" />
            </IconButton>
          </div>
        )}

        <Menu
          anchorEl={mainAnchorEl}
          open={newProdMenuOpen}
          id="addNew-menu"
          keepMounted
          onClose={() => setNewProdMenuOpen(false)}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          getContentAnchorEl={null}
          //className={classes.userMenu}
          transitionDuration={0}
        >
          <MenuItem onClick={() => addProduct()}>
            <ListItemText>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('externalProduct_add_financial', {
                    interpolation: { escapeValue: false },
                  }),
                }}
              />
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={() => addProduct(ExtProductCategory.PROPERTY)}>
            <ListItemText>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('externalProduct_add_property', {
                    interpolation: { escapeValue: false },
                  }),
                }}
              />
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={() => addProduct(ExtProductCategory.LOAN)}>
            <ListItemText>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('externalProduct_add_loan', {
                    interpolation: { escapeValue: false },
                  }),
                }}
              />
            </ListItemText>
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={mainAnchorEl}
          open={orderingOpen}
          id="sort-menu"
          keepMounted
          onClose={() => setOrderingOpen(false)}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          getContentAnchorEl={null}
          //className={classes.userMenu}
          transitionDuration={0}
        >
          <MenuItem onClick={() => setSorting(ExtProdSortBy.TITLE)}>
            <ListItemText>
              {t('externalProduct_sortBy')} {selectedLabel(ExtProdSortBy.TITLE)}
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={() => setSorting(ExtProdSortBy.INST)}>
            <ListItemText>
              {t('externalProduct_sortBy')} {selectedLabel(ExtProdSortBy.INST)}
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={() => setSorting(ExtProdSortBy.BALANCE)}>
            <ListItemText>
              {t('externalProduct_sortBy')} {selectedLabel(ExtProdSortBy.BALANCE)}
            </ListItemText>
          </MenuItem>
        </Menu>
      </Grid>
    </>
  );

  const header = (showCategory: ExtProductCategory, title: AggCategories, category?: ExtProductCategory) => (
    <Grid container spacing={0}>
      <Grid item>
        <Typography variant="h3" className={classes.moduleTitle}>
          {collapsed !== undefined ? (
            <Link
              className={classes.showLink}
              id="toggle-targets-link"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                toggleCategoryView(showCategory);
              }}
            >
              {t(`externalProducts_title_${title}`)}
              <span className={classes.count}> ({counts[title]})</span>
              <ArrowDropDownIcon
                htmlColor={'#000'}
                className={`${classes.showIcon} ${showDetail.includes(showCategory) ? classes.shownIcon : ''}`}
              />
            </Link>
          ) : (
            t(`externalProducts_title_${title}`)
          )}
        </Typography>
      </Grid>

      {!readOnly && data?.addingEnabled && !userMode && (
        <>
          <Grid item style={{ marginLeft: plan || overview ? undefined : '16px', marginBottom: '8px' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                addProduct(category);
              }}
              startIcon={<AddIcon fontSize="medium" />}
            >
              {t(`externalProducts_title_${title}_add`)}
            </Button>
          </Grid>
        </>
      )}

      {plan && showCategory === ExtProductCategory.FINANCIAL_PRODUCT && linkedAccounts && (
        <Tooltip title={t('externalProducts_refresh_tooltip')}>
          <IconButton onClick={extProductsStore.refreshProducts} style={{ padding: '8px', marginLeft: '16px' }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      )}
    </Grid>
  );

  const listProducts = (data: ExternalProductData[], filter: ExtProductCategory[]) => {
    return (
      <div className={extProdClasses.list}>
        {data
          .filter((p) => filter.includes(p.category))
          .sort((a, b) => sortExtProducts(a, b, sortBy, orderDir))
          .map((p) => (
            <ExternalProduct
              data={p}
              key={p.id}
              refresh={handleRefresh}
              store={extProductsStore}
              editProduct={editProduct}
              deleteProduct={deleteProduct}
              plan={plan}
              readOnly={readOnly}
              userMode={userMode}
              original={original}
              category={p.category}
              detail={viewMode !== ViewMode.WF}
            />
          ))}
      </div>
    );
  };
  const current =
    overview || plan
      ? [
          {
            title: currentAccount.title,
            id: 'current',
            periodic: { value: data?.currentDeposits, aggregated: true } as SimpleDeposit | undefined,
            balance: currentAccount.balance,
            interest: currentAccount.profit,
            parentId: ExtProductCategory.NONE as string,
            financialInstitution: { title: 'Edward', internal: true, id: '' },
            type: ExtProductType.CURRENT,
            lastUpdate: DateTime.local().toSQL(),
          },
        ]
      : [];

  const summary = [
    {
      title: '',
      id: 'summary',
      periodic: { value: data?.summary.deposit, aggregated: true } as SimpleDeposit | undefined,
      balance: data?.summary.balance,
    },
  ];

  return data ? (
    <>
      {!overview && mainHeader()}

      {tableView && !tablet && !overview ? (
        <Grid container>
          <Grid item xs={12} style={{ padding: '0 16px', marginBottom: '64px' }}>
            <ExternalProductsTable
              store={extProductsStore}
              readOnly={readOnly}
              userMode={userMode}
              original={original}
              data={[...current, ...data.externalProducts.map((p) => ({ ...p, id: p.id }))]}
              //data={current.concat(data.externalProducts.map((p) => ({ ...p, id: p.id })))}
              //data={data.externalProducts}
              editProduct={editProduct}
              deleteProduct={deleteProduct}
              header
              toggleFilterVisibility={toggleFilterVisibility}
              filtersVisible={filtersVisible}
            />
            {!filtersVisible && (
              <ExternalProductsTable
                store={extProductsStore}
                readOnly={readOnly}
                userMode={userMode}
                original={original}
                data={summary}
                //data={data.externalProducts}
                editProduct={editProduct}
                deleteProduct={deleteProduct}
                header={false}
              />
            )}
          </Grid>
        </Grid>
      ) : (
        <>
          {!overview && header(ExtProductCategory.FINANCIAL_PRODUCT, AggCategories.FINANCIAL)}

          <Collapse in={showDetail.includes(ExtProductCategory.FINANCIAL_PRODUCT)} className={classes.collapse}>
            <div style={{ marginBottom: '32px' }}>
              {listProducts(data.externalProducts, [ExtProductCategory.EDWARD, ExtProductCategory.FINANCIAL_PRODUCT])}
            </div>
          </Collapse>

          {!overview && header(ExtProductCategory.PROPERTY, AggCategories.PROPERTY, ExtProductCategory.PROPERTY)}

          <Collapse in={showDetail.includes(ExtProductCategory.PROPERTY)} className={classes.collapse}>
            <div style={{ marginBottom: '32px' }}>
              {listProducts(data.externalProducts, [ExtProductCategory.PROPERTY])}
            </div>
          </Collapse>

          {!overview && header(ExtProductCategory.LOAN, AggCategories.LOAN, ExtProductCategory.LOAN)}

          <Collapse in={showDetail.includes(ExtProductCategory.LOAN)} className={classes.collapse}>
            <div style={{ marginBottom: '32px' }}>{listProducts(data.externalProducts, [ExtProductCategory.LOAN])}</div>
          </Collapse>
        </>
      )}

      {isExtProductOpen && (
        <ExternalProductModal
          opened={isExtProductOpen}
          handleClose={handleProductModalClose}
          productId={productId}
          extProductsStore={extProductsStore}
          currency={currency}
          refresh={handleRefresh}
          options={data.options}
          defaultInflation={defaultInflation}
          defaultInflationType={defaultInflationType}
          key={productId}
          category={category}
          //        mode={mode}
        />
      )}

      {extProductsStore && refresh && (
        <ConfirmDialog
          noHeader
          isOpen={confirmDialog !== undefined}
          confirm={async () => {
            await extProductsStore?.deleteProduct(confirmDialog!);
            setConfirmDialog(undefined);
            refresh();
          }}
          close={() => {
            setConfirmDialog(undefined);
          }}
        >
          {t('externalProduct_confirmDelete')}
        </ConfirmDialog>
      )}
    </>
  ) : null;
};

export default compose(inject('rootStore'), observer)(ExternalProducts);
